import { definedMap } from "@glideapps/ts-necessities";
import type { PrimitiveValue } from "../components/computation-types";
import type { PrimitiveGlideTypeKind } from "../generator/description";

export type Unbound = null;
export const UnboundVal = null;

export function isBound<T>(x: T | Unbound): x is T {
    return x !== UnboundVal;
}

export function boundMap<T, U>(x: T | Unbound | undefined, f: (x: T) => U): U | Unbound | undefined {
    if (isBound(x)) {
        return definedMap(x, f);
    } else {
        return x;
    }
}

export interface WireComponentBase {
    readonly id?: string;
}

export interface WireAction {
    readonly token: string;
    readonly url?: string;
}

export interface WireMessage {
    readonly message: string;
    readonly title: string;
    readonly link?: {
        text: string;
        url: string;
    };
}

export interface WirePrimitiveValueWithType {
    readonly value: PrimitiveValue;
    readonly typeKind: PrimitiveGlideTypeKind;
}

export interface WireEditableValue<T> {
    readonly value: T;
    // If the token is `undefined` then editing should be disabled.
    readonly onChangeToken: string | undefined;
    // Validation error.  If it's an empty string, it means invalid, but don't
    // show an error message.
    readonly error?: string;
}

export interface WirePaging {
    readonly pageSize: number;
    readonly numPages: number;
    readonly pageIndex: WireEditableValue<number>;
}

export enum UIBackgroundStyle {
    None = "none",
    Card = "card",
    Soft = "soft",
    Highlight = "highlight",
    Image = "image",
    Dark = "dark",
    Accent = "accent",
}

export enum UIAlignment {
    Start = "start",
    Center = "center",
    End = "end",
    Stretch = "stretch",
}

export enum UIAspect {
    Auto = "aspect-auto",
    Square = "aspect-square",
    FourByThree = "aspect-four-by-three",
    SixteenByNine = "aspect-sixteen-by-nine",
    ThreeByOne = "aspect-three-by-one",
    ThreeByTwo = "aspect-three-by-two",
}

export enum UISize {
    Original = "size-original",
    Small = "size-small",
    Medium = "size-medium",
    Large = "size-large",
    Full = "size-full",
}

export type UISizeSimple = UISize.Small | UISize.Medium | UISize.Large;

export enum UIInsetStyle {
    Padded = "inset-padded",
    Fill = "inset-fill",
}

export enum ButtonBarSize {
    Auto = "auto",
    Wide = "wide",
}

export enum CardStyle {
    Card = "card",
    List = "list",
    Table = "table",
    Article = "article",
    Cover = "cover",
}

export enum HeroImageEffect {
    None = "None",
    Darken = "Darken",
    Lighten = "Lighten",
}

export enum HeroSize {
    Small = "size-sm",
    Medium = "size-md",
    Large = "size-lg",
    XLarge = "size-xl",
}

export enum SeparatorSize {
    Small = "Small",
    Medium = "Medium",
    Large = "Large",
}

export enum CustomComponentSource {
    URL = "url",
    InlineCode = "inline-code",
}

export enum CustomComponentSourceTransform {
    HTML = "html",
    React = "react",
}

export enum CustomComponentWidth {
    Full = "full",
    Container = "container",
}
